import { CircularProgress } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  CancelIcon,
  CheckCircleIcon,
  FolderZipOutlinedIcon,
} from "@/shared/foundation/icons/mui";

import { UIExportJob } from "../types";

import styles from "./ExportJobRow.module.css";

interface ExportJobProgressIndicatorProps {
  status: UIExportJob["status"];
  progress: number;
  isTerminal: boolean;
  isError: boolean;
}

const ExportJobProgressIndicator: React.FC<ExportJobProgressIndicatorProps> = ({
  status,
  progress,
  isTerminal,
  isError,
}) => {
  if (status === "completed") {
    return <CheckCircleIcon color="success" />;
  }
  if (isError) {
    return <CancelIcon color="error" />;
  }
  if (progress > 0) {
    return (
      <div className={styles.progressWrapper}>
        <CircularProgress
          variant="determinate"
          sx={(t) => ({
            color: t.palette.grey[200],
            ...t.applyStyles("dark", { color: t.palette.grey[800] }),
          })}
          size={20}
          thickness={4}
          value={100}
        />
        <div className={styles.progressBack}>
          <CircularProgress
            variant="determinate"
            size={20}
            thickness={4}
            value={progress}
          />
        </div>
      </div>
    );
  }
  // pending / processing with no progress yet — indeterminate spinner
  return (
    <CircularProgress
      size={20}
      thickness={4}
      sx={{ color: isTerminal ? "lightgrey" : undefined, mr: 0.25 }}
      variant={isTerminal ? "determinate" : "indeterminate"}
      value={isTerminal ? 100 : undefined}
    />
  );
};

interface ExportJobRowProps {
  job: UIExportJob;
}

export const ExportJobRow: React.FC<ExportJobRowProps> = ({ job }) => {
  const rawProgress =
    job.totalBytes > 0 ? (job.bytesUploaded / job.totalBytes) * 100 : 0;
  const progress = Math.min(rawProgress, 99);
  const isTerminal =
    job.status === "completed" ||
    job.status === "failed" ||
    job.status === "expired";
  const isError = job.status === "failed" || job.status === "expired";

  return (
    <div className={classNames(styles.row, { [styles.rowError]: isError })}>
      <div className={styles.left}>
        <FolderZipOutlinedIcon color={isError ? "error" : "info"} />
        <RobotoTypography noWrap className={styles.label}>
          {job.error ?? job.label}
        </RobotoTypography>
      </div>
      <ExportJobProgressIndicator
        status={job.status}
        progress={progress}
        isTerminal={isTerminal}
        isError={isError}
      />
    </div>
  );
};
